/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import type { CSSProps } from '@compiled/react';
import type { IntlShape } from 'react-intl';

import { imageBorderMessages as messages } from '@atlaskit/editor-common/media/imageBorder';
import Popup from '@atlaskit/editor-common/Popup';
import { DropdownMenuSharedCssClassName } from '@atlaskit/editor-common/styles/dropdown-menu';
import type { Icon } from '@atlaskit/editor-common/types/floating-toolbar';
import ColorPalette from '@atlaskit/editor-common/ui-color/ColorPalette';
import borderColorPalette from '@atlaskit/editor-common/ui-color/ColorPalette/borderColorPalette';
import { borderPaletteTooltipMessages } from '@atlaskit/editor-common/ui-color/ColorPalette/paletteMessagesTokenModeNames';
import { ArrowKeyNavigationProvider } from '@atlaskit/editor-common/ui-menu/ArrowKeyNavigationProvider';
import { ArrowKeyNavigationType } from '@atlaskit/editor-common/ui-menu/ArrowKeyNavigationProvider/types';
import DropdownMenu from '@atlaskit/editor-common/ui-menu/DropdownMenu';
import type { MenuItem } from '@atlaskit/editor-common/ui-menu/DropdownMenu/types';
import ToolbarButton from '@atlaskit/editor-common/ui-menu/ToolbarButton';
import { hexToEditorBorderPaletteColor } from '@atlaskit/editor-palette/border';
import BorderIcon from '@atlaskit/icon/core/border';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import StrokeWeightLargeIcon from '@atlaskit/icon/core/stroke-weight-large';
import StrokeWeightMediumIcon from '@atlaskit/icon/core/stroke-weight-medium';
import StrokeWeightSmallIcon from '@atlaskit/icon/core/stroke-weight-small';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

// menuItemDimensions and itemSpacing are copied from
// packages/editor/editor-core/src/plugins/floating-toolbar/ui/DropdownMenu.tsx
const menuItemDimensions = {
	width: 175,
	height: 32,
};
const itemSpacing = 4;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @compiled/no-exported-css -- Ignored via go/DSP-18766
const contextualMenuArrow: CSSProps<unknown> = css({
	display: 'flex',
	'&::after': {
		content: "'›'",
		marginLeft: token('space.050'),
		color: token('color.icon'),
	},
});

// top is set via inline style per usage (dynamic index not extractable by @compiled)
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @compiled/no-exported-css, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
const contextualSubMenuBase: CSSProps<unknown> = css({
	borderRadius: token('radius.small', '3px'),
	backgroundColor: token('elevation.surface.overlay'),
	boxShadow: token('elevation.shadow.overlay'),
	display: 'flex',
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	left: `${menuItemDimensions.width}px`,
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		padding: 0,
	},
});
// Pre-computed top offsets for submenu indices 0 and 1
const contextualSubMenuTop0 = 0;
const contextualSubMenuTop1 = menuItemDimensions.height + itemSpacing * 2;

// backgroundColor is set via inline style (selected state not extractable by @compiled)
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @compiled/no-exported-css, @atlaskit/design-system/use-tokens-shape -- Ignored via go/DSP-18766
const buttonStyleBase: CSSProps<unknown> = css({
	height: '26px',
	width: '26px',
	padding: 0,
	borderRadius: token('radius.small'),
	border: `1px solid ${token('color.border')}`,
	cursor: 'pointer',
	display: 'block',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @compiled/no-exported-css, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
const buttonWrapperStyle: CSSProps<unknown> = css({
	borderColor: 'transparent',
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	marginTop: token('space.025'),
	marginRight: token('space.075'),
	marginBottom: token('space.025'),
	marginLeft: token('space.025'),
	overflow: 'hidden',
	display: 'flex',
	alignItems: 'center',
	paddingTop: token('space.025'),
	paddingRight: token('space.025'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.025'),
	borderRadius: token('radius.medium'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:focus-within, &:focus, &:hover': {
		borderColor: token('color.border'),
	},
});

// Static base — enabled/isOpen hover states applied via separate conditional css objects
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @compiled/no-exported-css, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
const toolbarButtonWrapperBase: CSSProps<unknown> = css({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.native-embed-border-toolbar-btn': {
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
		padding: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > span': { marginTop: 0, marginRight: 0, marginBottom: 0, marginLeft: 0 },
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Required for toolbar button styling
		'&:not([disabled])::after': { border: 'none' },
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.native-embed-border-toolbar-dropdown': {
		padding: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > span': { marginTop: 0, marginRight: 0, marginBottom: 0, marginLeft: 0 },
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		width: '16px !important',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		borderTopLeftRadius: '0 !important',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		borderBottomLeftRadius: '0 !important',
		marginLeft: token('space.025'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Required for toolbar button styling
		'&:not([disabled])::after': { border: 'none' },
	},
});

// Applied when !enabled: show hover highlight on the main button
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @compiled/no-exported-css, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
const toolbarButtonWrapperHoverBtn: CSSProps<unknown> = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:hover .native-embed-border-toolbar-btn': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

// Applied when !isOpen && !enabled: show hover highlight on the dropdown button
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @compiled/no-exported-css, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
const toolbarButtonWrapperHoverDropdown: CSSProps<unknown> = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:hover .native-embed-border-toolbar-dropdown': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @compiled/no-exported-css, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
const dropdownOptionButton: CSSProps<unknown> = css({
	backgroundColor: 'transparent',
	borderWidth: token('border.width.selected'),
	borderStyle: 'solid',
	borderColor: 'transparent',
	display: 'flex',
	width: '100%',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: 0,
	'&:focus': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		borderColor: token('color.border.focused'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @compiled/no-exported-css, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
const dropdownWrapper: CSSProps<unknown> = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"span[role='menuitem']": {
		padding: 0,
	},
});

export interface NativeEmbedBorderProps {
	/** Current border color (hex). Undefined means no border. */
	borderColor?: string;
	/** Current border size: 1=Subtle, 2=Medium, 3=Bold. */
	borderSize?: number;
	intl: IntlShape;
	/** Called when the user selects a new color from the palette. */
	setBorderColor: (color: string) => void;
	/** Called when the user selects a new size. */
	setBorderSize: (size: number) => void;
	/** Called when the border toggle button is clicked (add/remove border). */
	toggleBorder: () => void;
}

const NativeEmbedBorder = ({
	intl: { formatMessage },
	borderColor,
	borderSize,
	toggleBorder,
	setBorderColor,
	setBorderSize,
}: NativeEmbedBorderProps): React.JSX.Element => {
	const popupTarget = useRef<HTMLDivElement>(null);
	const dropDownColorOptionButton = useRef<HTMLButtonElement>(null);
	const dropDownSizeOptionButton = useRef<HTMLButtonElement>(null);
	const colorSubmenuRef = useRef<HTMLDivElement>(null);
	const sizeSubmenuRef = useRef<HTMLDivElement>(null);
	const openDropdownButtonRef = useRef<HTMLButtonElement>(null);
	const enabled = !!borderColor;
	const [isOpen, setIsOpen] = useState(false);
	const [isOpenByKeyboard, setIsOpenedByKeyboard] = useState(false);
	const [isColorSubmenuOpen, setIsColorSubmenuOpen] = useState(false);
	const [isSizeSubmenuOpen, setIsSizeSubmenuOpen] = useState(false);

	const handleColorSubmenuEsc = useCallback(() => {
		setIsOpenedByKeyboard(false);
		setIsColorSubmenuOpen(false);
		dropDownColorOptionButton?.current?.focus();
	}, []);

	const handleSizeSubmenuEsc = useCallback(() => {
		setIsOpenedByKeyboard(false);
		setIsSizeSubmenuOpen(false);
		dropDownSizeOptionButton?.current?.focus();
	}, []);

	const handleSubMenuRef = (ref: HTMLDivElement | null) => {
		if (!ref) {
			return;
		}
		const rect = ref.getBoundingClientRect();
		if (rect.left + rect.width > window.innerWidth) {
			ref.style.left = `-${rect.width}px`;
		}
	};

	const handleTriggerByKeyboard = (
		event: React.KeyboardEvent,
		allowedKeys: string[],
		callback: () => void,
	) => {
		if (!allowedKeys.includes(event.key)) {
			return;
		}
		event.preventDefault();
		callback();
		setIsOpenedByKeyboard(true);
	};

	const handleTriggerToolbarByKeyboard = (event: React.KeyboardEvent, callback: () => void) => {
		handleTriggerByKeyboard(event, ['Enter', ' '], callback);
	};

	const handleTriggerSubmenuByKeyboard = (event: React.KeyboardEvent, callback: () => void) => {
		handleTriggerByKeyboard(event, ['Enter', 'ArrowRight'], callback);
	};

	useEffect(() => {
		const focusFirstOption = (submenuRef: React.RefObject<HTMLDivElement>, isOpen: boolean) => {
			if (!isOpenByKeyboard) {
				return;
			}
			if (isOpen && submenuRef.current) {
				const firstOption = submenuRef.current.querySelector('button');
				if (!firstOption) {
					return;
				}
				firstOption.focus();
				const keyboardEvent = new KeyboardEvent('keydown', {
					key: 'ArrowDown',
					bubbles: true,
				});
				firstOption.dispatchEvent(keyboardEvent);
			}
		};
		focusFirstOption(colorSubmenuRef, isColorSubmenuOpen);
		focusFirstOption(sizeSubmenuRef, isSizeSubmenuOpen);
	}, [isColorSubmenuOpen, isSizeSubmenuOpen, isOpenByKeyboard]);

	const borderSizeOptions: { icon: Icon; name: string; value: number }[] = [
		{
			name: formatMessage(messages.borderSizeSubtle),
			value: 1,
			icon: StrokeWeightSmallIcon,
		},
		{
			name: formatMessage(messages.borderSizeMedium),
			value: 2,
			icon: StrokeWeightMediumIcon,
		},
		{
			name: formatMessage(messages.borderSizeBold),
			value: 3,
			icon: StrokeWeightLargeIcon,
		},
	];

	const items: MenuItem[] = [
		{
			content: (
				<div>
					<button
						ref={dropDownColorOptionButton}
						type="button"
						aria-label={formatMessage(messages.borderColorDropdownAriaLabel)}
						data-testid="native-embed-border-dropdown-button-color"
						css={dropdownOptionButton}
						aria-expanded={isColorSubmenuOpen}
						onKeyDown={(e) =>
							handleTriggerSubmenuByKeyboard(e, () => {
								setIsColorSubmenuOpen((prev) => {
									const next = !prev;
									if (next) {
										setIsSizeSubmenuOpen(false);
									}
									return next;
								});
							})
						}
					>
						<Text>{formatMessage(messages.borderColor)}</Text>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
						<div css={contextualMenuArrow} />
					</button>
					<div
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={DropdownMenuSharedCssClassName.SUBMENU}
						ref={colorSubmenuRef}
					>
						{isColorSubmenuOpen && (
							<div
								css={contextualSubMenuBase}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic top offset required
								style={{ top: `${contextualSubMenuTop0}px` }}
								ref={handleSubMenuRef}
							>
								<ArrowKeyNavigationProvider
									type={ArrowKeyNavigationType.MENU}
									// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
									handleClose={(e) => {
										e.preventDefault();
										e.stopPropagation();
										handleColorSubmenuEsc();
									}}
									disableCloseOnArrowClick={true}
								>
									<ColorPalette
										// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
										onClick={(color: string) => {
											setBorderColor(color);
											setIsOpen(false);
										}}
										// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
										onKeyDown={(color, _, event) => {
											if (event.key === 'Enter' || event.key === ' ') {
												setBorderColor(color);
												setIsOpen(false);
												setIsColorSubmenuOpen(false);
												setIsSizeSubmenuOpen(false);
												openDropdownButtonRef.current?.focus();
											}
										}}
										selectedColor={borderColor ?? null}
										// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
										paletteOptions={{
											palette: borderColorPalette,
											paletteColorTooltipMessages: borderPaletteTooltipMessages,
											hexToPaletteColor: hexToEditorBorderPaletteColor,
										}}
									/>
								</ArrowKeyNavigationProvider>
							</div>
						)}
					</div>
				</div>
			),
			'data-testid': 'dropdown-item__Color',
			key: 'dropdown-menu-native-embed-border-color-button',
			value: { name: 'color' },
			'aria-label': '',
			wrapperTabIndex: null,
		},
		{
			content: (
				<div>
					<button
						type="button"
						aria-label={formatMessage(messages.borderSizeDropdownAriaLabel)}
						data-testid="native-embed-border-dropdown-button-size"
						/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */
						css={dropdownOptionButton}
						aria-expanded={isSizeSubmenuOpen}
						ref={dropDownSizeOptionButton}
						onKeyDown={(e) =>
							handleTriggerSubmenuByKeyboard(e, () => {
								setIsSizeSubmenuOpen((prev) => {
									const next = !prev;
									if (next) {
										setIsColorSubmenuOpen(false);
									}
									return next;
								});
							})
						}
					>
						<Text>{formatMessage(messages.borderSize)}</Text>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
						<div css={contextualMenuArrow} />
					</button>
					<div
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={DropdownMenuSharedCssClassName.SUBMENU}
						ref={sizeSubmenuRef}
					>
						{isSizeSubmenuOpen && (
							<ArrowKeyNavigationProvider
								type={ArrowKeyNavigationType.MENU}
								// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
								handleClose={(e) => {
									e.preventDefault();
									handleSizeSubmenuEsc();
								}}
								disableCloseOnArrowClick={true}
							>
								<div
									css={contextualSubMenuBase}
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic top offset required
									style={{ top: `${contextualSubMenuTop1}px` }}
									ref={handleSubMenuRef}
								>
									{borderSizeOptions.map(({ name, value, icon }, idx) => {
										// Ignored via go/ees005
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
										const ButtonIcon = icon as React.ComponentClass<any>;
										return (
											<Tooltip key={value} content={name}>
												{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
												<span css={buttonWrapperStyle}>
													<button
														type="button"
														css={buttonStyleBase}
														// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic backgroundColor for selected state
														style={{
															backgroundColor:
																value === borderSize
																	? token('color.text')
																	: token('color.background.neutral'),
														}}
														aria-label={name}
														role="radio"
														aria-checked={value === borderSize}
														onClick={() => {
															setBorderSize(value);
															setIsOpen(false);
														}}
														onKeyDown={(event) => {
															if (event.key === 'Enter' || event.key === ' ') {
																setBorderSize(value);
																setIsOpen(false);
																setIsColorSubmenuOpen(false);
																setIsSizeSubmenuOpen(false);
																openDropdownButtonRef.current?.focus();
															}
														}}
														onMouseDown={(e) => {
															e.preventDefault();
														}}
													>
														<ButtonIcon
															color={
																value === borderSize ? token('color.icon.inverse') : 'currentColor'
															}
															spacing="spacious"
															label=""
														/>
													</button>
												</span>
											</Tooltip>
										);
									})}
								</div>
							</ArrowKeyNavigationProvider>
						)}
					</div>
				</div>
			),
			'data-testid': 'dropdown-item__Size',
			key: 'dropdown-menu-native-embed-border-size-button',
			value: { name: 'size' },
			'aria-label': '',
			wrapperTabIndex: null,
		},
	];

	const fitTolerance = 10;
	const fitWidth = menuItemDimensions.width;
	const fitHeight = items.length * (menuItemDimensions.height + itemSpacing);
	const isAnySubMenuOpen = isSizeSubmenuOpen || isColorSubmenuOpen;

	return (
		<div>
			<div
				css={[
					toolbarButtonWrapperBase,
					!enabled && toolbarButtonWrapperHoverBtn,
					!isOpen && !enabled && toolbarButtonWrapperHoverDropdown,
				]}
			>
				<ToolbarButton
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop, @atlaskit/design-system/no-unsafe-style-overrides -- className required to target CSS selectors in toolbarButtonWrapper
					className="native-embed-border-toolbar-btn"
					selected={enabled}
					onClick={toggleBorder}
					spacing="compact"
					aria-label={
						enabled ? formatMessage(messages.removeBorder) : formatMessage(messages.addBorder)
					}
					iconBefore={<BorderIcon color="currentColor" label="" spacing="spacious" />}
					title={enabled ? formatMessage(messages.removeBorder) : formatMessage(messages.addBorder)}
				/>
				<div ref={popupTarget}>
					<ToolbarButton
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop, @atlaskit/design-system/no-unsafe-style-overrides -- className required to target CSS selectors in toolbarButtonWrapper
						className="native-embed-border-toolbar-dropdown"
						ref={openDropdownButtonRef}
						selected={enabled || isOpen}
						aria-expanded={isOpen}
						aria-label={formatMessage(messages.borderOptions)}
						title={formatMessage(messages.borderOptions)}
						spacing="compact"
						iconBefore={
							<ChevronDownIcon color="currentColor" spacing="spacious" label="" size="small" />
						}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						onClick={() => {
							setIsOpen(!isOpen);
							setIsOpenedByKeyboard(false);
						}}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						onKeyDown={(e) => handleTriggerToolbarByKeyboard(e, () => setIsOpen(!isOpen))}
					/>
				</div>
			</div>
			{isOpen && (
				<Popup
					target={popupTarget.current ? popupTarget.current : undefined}
					fitWidth={fitWidth + fitTolerance}
					fitHeight={fitHeight + fitTolerance}
					forcePlacement={true}
					stick={true}
				>
					<div
						onMouseLeave={() => {
							setIsColorSubmenuOpen(false);
							setIsSizeSubmenuOpen(false);
						}}
						onBlur={() => {
							setIsColorSubmenuOpen(false);
							setIsSizeSubmenuOpen(false);
						}}
						/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */
						css={dropdownWrapper}
					>
						<DropdownMenu
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							arrowKeyNavigationProviderOptions={{
								type: ArrowKeyNavigationType.MENU,
								disableArrowKeyNavigation: isAnySubMenuOpen,
							}}
							allowEnterDefaultBehavior={isAnySubMenuOpen}
							handleEscapeKeydown={
								isAnySubMenuOpen
									? () => {
											return;
										}
									: () => {
											openDropdownButtonRef.current?.focus();
										}
							}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							items={[{ items }]}
							isOpen={isOpen}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							shouldFocusFirstItem={() => isOpenByKeyboard}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							onOpenChange={() => {
								setIsOpen(false);
								setIsColorSubmenuOpen(false);
								setIsSizeSubmenuOpen(false);
								setIsOpenedByKeyboard(false);
							}}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							onItemActivated={({ item }) => {
								if (item.value.name === 'color') {
									setIsColorSubmenuOpen((prev) => {
										const next = !prev;
										if (next) {
											setIsSizeSubmenuOpen(false);
										}
										return next;
									});
								}
								if (item.value.name === 'size') {
									setIsSizeSubmenuOpen((prev) => {
										const next = !prev;
										if (next) {
											setIsColorSubmenuOpen(false);
										}
										return next;
									});
								}
							}}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							onMouseEnter={({ item }) => {
								if (item.value.name === 'color') {
									setIsColorSubmenuOpen(true);
									setIsOpenedByKeyboard(false);
								}
								if (item.value.name === 'size') {
									setIsSizeSubmenuOpen(true);
									setIsOpenedByKeyboard(false);
								}
							}}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							onMouseLeave={({ item }) => {
								if (item.value.name === 'color') {
									setIsColorSubmenuOpen(false);
								}
								if (item.value.name === 'size') {
									setIsSizeSubmenuOpen(false);
								}
							}}
							fitWidth={fitWidth + fitTolerance}
							fitHeight={fitHeight + fitTolerance}
						/>
					</div>
				</Popup>
			)}
		</div>
	);
};

export default NativeEmbedBorder;
